'use client';

import type { CurrentUserResponseDto } from '@/apis/backend/generated/models';

import React from 'react';
import { create } from 'zustand';
import { setAuthToken } from '@/lib/api';
import { CurrentUserResponseDtoUserType } from '@/apis/backend/generated/models';
import { applyPendingInviteTenant, clearPendingInviteTenant } from '@/lib/active-tenant';
import { invitationsControllerAccept } from '@/apis/backend/generated/invitations/invitations';
import {
  authControllerLogout,
  authControllerRefresh,
  authControllerGetCurrentUser,
} from '@/apis/backend/generated/auth/auth';

/* -------------------------------------------------------------------------- */
/* Constants                                                                  */
/* -------------------------------------------------------------------------- */

const ACCESS_TOKEN_KEY = 'nexmail_access_token';
const REFRESH_TOKEN_KEY = 'nexmail_refresh_token';
const IMPERSONATION_TOKEN_KEY = 'impersonation_token';

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

export type AuthState = 'loading' | 'unauthenticated' | 'authenticated';

interface AuthStore {
  // State
  state: AuthState;
  dbUser: CurrentUserResponseDto | null;
  accessToken: string | null;
  refreshToken: string | null;

  // Actions
  setLoading: () => void;
  setUnauthenticated: () => void;
  setAuthenticated: (dbUser: CurrentUserResponseDto) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setTokensAndFetchUser: (accessToken: string, refreshToken: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<boolean>;

  // Internal
  _initialized: boolean;
  _initComplete: boolean;
  _setInitialized: () => void;
  _setInitComplete: () => void;
}

/* -------------------------------------------------------------------------- */
/* Store                                                                      */
/* -------------------------------------------------------------------------- */

export const useAuthStore = create<AuthStore>((set, get) => ({
  // Initial State
  state: 'loading',
  dbUser: null,
  accessToken: null,
  refreshToken: null,
  _initialized: false,
  _initComplete: false,

  // State Transitions
  setLoading: () => {
    set({ state: 'loading' });
  },

  setUnauthenticated: () => {
    set({
      state: 'unauthenticated',
      dbUser: null,
      accessToken: null,
      refreshToken: null,
    });
  },

  setAuthenticated: (dbUser: CurrentUserResponseDto) => {
    set({
      state: 'authenticated',
      dbUser,
    });
  },

  setTokens: (accessToken: string, refreshToken: string) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    // A fresh token pair means a clean identity — drop any stale impersonation
    // token so the request interceptor doesn't keep preferring it.
    localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
    setAuthToken(accessToken);
    set({ accessToken, refreshToken });
  },

  // Store tokens, push to axios, and fetch the current user
  setTokensAndFetchUser: async (accessToken: string, refreshToken: string) => {
    get().setTokens(accessToken, refreshToken);

    try {
      const dbUser = await authControllerGetCurrentUser();
      if (dbUser) {
        // Check for a pending invitation token and accept it
        const pendingToken = localStorage.getItem('pendingInvitationToken');
        if (pendingToken) {
          try {
            await invitationsControllerAccept(pendingToken);
            localStorage.removeItem('pendingInvitationToken');
            // Land the user on the org/client they were just invited to.
            applyPendingInviteTenant();
            const refreshedDbUser = await authControllerGetCurrentUser();
            if (refreshedDbUser) {
              get().setAuthenticated(refreshedDbUser);
              return;
            }
          } catch (inviteErr) {
            console.warn('[AuthStore] Failed to accept invitation:', inviteErr);
            localStorage.removeItem('pendingInvitationToken');
            clearPendingInviteTenant();
          }
        }

        get().setAuthenticated(dbUser);
      }
    } catch (error) {
      console.error('[AuthStore] Failed to fetch user after setting tokens:', error);
      throw error;
    }
  },

  // Logout
  logout: async () => {
    const { accessToken } = get();
    try {
      if (accessToken) {
        await authControllerLogout();
      }
    } catch {
      // Ignore logout errors -- clear local state regardless
    }

    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
    setAuthToken(null);
    get().setUnauthenticated();
  },

  // Refresh tokens and re-fetch user data.
  // Returns true on success, false on failure. Never throws.
  refresh: async (): Promise<boolean> => {
    const storedRefreshToken =
      get().refreshToken || localStorage.getItem(REFRESH_TOKEN_KEY);

    if (!storedRefreshToken) {
      get().setUnauthenticated();
      return false;
    }

    try {
      const data = await authControllerRefresh({
        refreshToken: storedRefreshToken,
      });

      if (data?.accessToken && data?.refreshToken) {
        get().setTokens(data.accessToken, data.refreshToken);

        // Re-fetch user data with new token
        const dbUser = await authControllerGetCurrentUser();
        if (dbUser) {
          get().setAuthenticated(dbUser);
          return true;
        }
      }

      get().setUnauthenticated();
      return false;
    } catch {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
      setAuthToken(null);
      get().setUnauthenticated();
      return false;
    }
  },

  // Internal
  _setInitialized: () => set({ _initialized: true }),
  _setInitComplete: () => set({ _initComplete: true }),
}));

/* -------------------------------------------------------------------------- */
/* Hook                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Drop the impersonation token and land back on the admin screens.
 *
 * A hard navigation rather than a router push: every cached query was fetched
 * as the impersonated user, and a soft transition would keep serving those rows
 * to the admin.
 */
export function stopImpersonation() {
  localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
  window.location.href = '/admin-skidog';
}

export function useAuth() {
  const state = useAuthStore((s) => s.state);
  const dbUser = useAuthStore((s) => s.dbUser);
  const logout = useAuthStore((s) => s.logout);
  const refresh = useAuthStore((s) => s.refresh);
  const initComplete = useAuthStore((s) => s._initComplete);

  return {
    state,
    dbUser,
    logout,
    refresh,
    isLoading: state === 'loading',
    isAuthenticated: state === 'authenticated',
    // True after first auth check completes (can now trust state value)
    initComplete,

    // Admin & Impersonation helpers
    isPlatformAdmin: dbUser?.userType === CurrentUserResponseDtoUserType.admin,
    // A NexMail employee account. Not a member of any organization, so every
    // membership-derived check is false for them -- reach comes from the work
    // they hold, resolved server-side.
    isStaff: dbUser?.userType === CurrentUserResponseDtoUserType.staff,
    isImpersonating: dbUser?.isImpersonating === true,
    realAdmin: dbUser?.realAdmin,
    stopImpersonation,
  };
}

/* -------------------------------------------------------------------------- */
/* Initialization                                                             */
/* -------------------------------------------------------------------------- */

/** Race a promise against a timeout. */
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms),
    ),
  ]);
}

const AUTH_TIMEOUT_MS = 10_000;

export async function initializeAuth() {
  const store = useAuthStore.getState();

  if (store._initialized) {
    return;
  }

  store._setInitialized();

  const storedAccessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
  const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

  // No tokens stored -- unauthenticated
  if (!storedAccessToken) {
    store.setUnauthenticated();
    store._setInitComplete();
    return;
  }

  // Push token to axios interceptor cache
  setAuthToken(storedAccessToken);
  useAuthStore.setState({
    accessToken: storedAccessToken,
    refreshToken: storedRefreshToken,
  });

  try {
    // Try to fetch current user with stored access token
    const dbUser = await withTimeout(
      authControllerGetCurrentUser(),
      AUTH_TIMEOUT_MS,
      'getCurrentUser',
    );

    if (!dbUser) {
      console.warn('[AuthStore] No DB user returned');
      store.setUnauthenticated();
      store._setInitComplete();
      return;
    }

    // Check for a pending invitation token and accept it
    const pendingToken = localStorage.getItem('pendingInvitationToken');
    if (pendingToken) {
      try {
        await invitationsControllerAccept(pendingToken);
        localStorage.removeItem('pendingInvitationToken');
        // Land the user on the org/client they were just invited to.
        applyPendingInviteTenant();
        // Re-fetch the DB user so it includes the new org membership
        const refreshedDbUser = await authControllerGetCurrentUser();
        if (refreshedDbUser) {
          store.setAuthenticated(refreshedDbUser);
          store._setInitComplete();
          return;
        }
      } catch (inviteErr) {
        console.warn('[AuthStore] Failed to accept invitation:', inviteErr);
        localStorage.removeItem('pendingInvitationToken');
        clearPendingInviteTenant();
      }
    }

    // Success
    store.setAuthenticated(dbUser);
  } catch (error: any) {
    // If 401, try refreshing the token
    if (error?.statusCode === 401 || error?.status === 401) {
      if (storedRefreshToken) {
        const ok = await withTimeout(store.refresh(), AUTH_TIMEOUT_MS, 'tokenRefresh').catch(() => false);
        if (ok) {
          store._setInitComplete();
          return;
        }
      }
    }

    console.error('[AuthStore] Error during initialization:', error);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
    setAuthToken(null);
    store.setUnauthenticated();
  }

  store._setInitComplete();
}

/* -------------------------------------------------------------------------- */
/* Provider                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * When the tab becomes visible after being hidden, silently re-validate the
 * session.  If the access token has expired while the tab was in the
 * background, try a refresh.  If that also fails, push the user to sign-in
 * instead of leaving them on a broken page.
 */
function useVisibilityRevalidation() {
  React.useEffect(() => {
    let hidden = document.hidden;

    const handler = async () => {
      // Only act on hidden -> visible transitions
      if (hidden && !document.hidden) {
        hidden = false;
        const { state, accessToken, refreshToken } = useAuthStore.getState();
        if (state !== 'authenticated' || !accessToken) return;

        const valid = await withTimeout(authControllerGetCurrentUser(), AUTH_TIMEOUT_MS, 'revalidate').then(() => true).catch(() => false);
        if (valid) return;

        // Token expired while tab was hidden -- try refresh
        if (refreshToken) {
          const ok = await useAuthStore.getState().refresh();
          if (ok) return;
        }

        // Could not recover -- force sign-in
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
        setAuthToken(null);
        useAuthStore.getState().setUnauthenticated();
      } else {
        hidden = document.hidden;
      }
    };

    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    initializeAuth();
    setMounted(true);
  }, []);

  useVisibilityRevalidation();

  if (!mounted) {
    return null;
  }

  return <>{children}</>;
}
